import { Fragment, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useConfig, useSetConfig } from "../api/queries";
import type { JsonValue } from "../api/schemas";
import { useAuth, useCan } from "../auth/AuthContext";
import { tableStyles } from "../components/table";
import { nextDrops } from "../lib/arena";
import { Button, Chip, ConfirmDialog, EmptyState, ErrorState, Loading, PageHeader, TextInput } from "../components/ui";
import styles from "./pages.module.css";

const show = (v: JsonValue) => JSON.stringify(v);

/** What each known key does (the API returns values only). */
const DESCRIPTIONS: Record<string, string> = {
  "arena.schedule": "Arena drop times in UTC (HH:MM). Each lobby opens 10 minutes before; a drop needs 20 players at T0 or everyone rolls into the next one.",
  "arena.devEveryMinutes": "Dev and QA only: a drop every N minutes (1–1440), overriding the schedule, with labelled BOT players. null in production.",
  "daily.requireApproval": "When true, /v1/daily/today only serves approved or published puzzles.",
  "features.teams": "Teams (Phase 3). Clients read it from GET /v1/config.",
  "features.arena": "The Arena · Cutdown tab (Phase 4). Clients read it from GET /v1/config.",
  "features.duels": "Duels and private rooms (Phase 4). Clients read it from GET /v1/config.",
  "features.gameCenter": "Game Center mirroring on iOS (daily score, weekly XP, badges as achievements). Off until App Store Connect has the IDs.",
  "levels.freeDailyAllowance": "Levels a free player can play per day; null means unlimited.",
  "notifications.dailyCap": "Most notifications a player gets per day, local and server combined.",
  "notifications.quietHours": "No notifications between start and end, in the player's local time.",
  // Ads (contracts/ads.md §2): edited on Monetisation → Ads, where editors may change formats and placements.
  "ads.enabled": "Master switch for ads for free players. Pro and Lifetime never see ads. Easier on Monetisation → Ads.",
  "ads.provider": "admanager (AdX through Google Ad Manager) | admob | unity (reserved, ads off).",
  "ads.testMode": "Serve Google's test units for the provider whatever the id fields say.",
  "ads.admob": "AdMob app id (built into the app) and unit ids ca-app-pub-…/….",
  "ads.admanager": "Ad Manager app id (optional: GADIsAdManagerApp) and ad unit paths /<network>/<unit>.",
  "ads.formats": "Formats the app may show: banner, interstitial, rewarded, appOpen (editor+ on the Ads page).",
  "ads.placements": "Per placement: enabled, reward, caps and intervals (editor+ on the Ads page).",
  "ads.newUserGraceDays": "Days after install with no interstitial, banner or app-open ads.",
  "ads.countries": "all | allow | deny, with ISO country codes.",
  "ads.rewards.requireSsv": "AdMob only: grant rewards after Google's SSV callback (https://api.iqdaily.app/v1/ads/ssv).",
  "ads.web": "Ads on the web player: enabled, the Ad Manager network code, web unit paths /<network>/<unit> (rewarded, banner, interstitial), consent google | none. Easier on Monetisation → Ads.",
  // Lume (contracts/lume.md): easier on the Automation and Ask Lume pages.
  "lume.pushTriggers": "Lume engagement pushes: come-back after 2/5/12 days, Review due, weekly Focus. Easier on Automation.",
  "lume.chat": "Ask Lume: enabled, questions per local day (free / Pro), the Claude model, reply length, monthly budget. Easier on Ask Lume.",
  "cups.enabled": "Tournaments for players: the app's features.cups is cups.enabled and not cups.hidden. Weekly Cups are created only while it's on. Easier on Tournaments → Settings.",
  "cups.hidden": "Hide tournaments in the app: no cards, sections, settings, links or pushes, while cups keep running so you can show them again. Easier on Tournaments → Settings.",
  "cups.weekly": "The Weekly Cup template: opens (weekday + UTC time), duration, brackets, set size, prizes, text. The cups job keeps the next 2 created (visible to players). Easier on Tournaments → Settings.",
  "cups.excludedCountries": "Storefront countries (ISO alpha-2) where cups can't be entered.",
  "cups.prizeExcludedCountries": "Storefront countries that can play but can't win money prizes (gift cards, other).",
  "cups.claimDays": "Days a winner has to claim a money prize after the results are confirmed (default 14).",
  "paywall.onOpen": "The paywall as the app opens: after onboarding, and for returning free players (never / every open / daily / every N days, hours apart). Easier on Monetisation → Paywall settings.",
  "paywall.defaultVariant": "The paywall players outside a running experiment see: layout, copy, plans, products, savings badge, close delay, exit offer.",
  "paywall.allowedProducts": "Product ids experiments may swap in for a plan (same subscription group, live in App Store Connect).",
  "paywall.exitOffer": "The one-time exit offer: on/off, the discounted product, its text. Off until the product is approved with a build.",
  "paywall.promoOffers": "Promotional offers for lapsed or non-renewing subscribers (App Store Connect offer ids); the first match shows.",
  "paywall.prices": "USD list price per product, for the revenue estimates (Monetisation → Revenue, the experiments' results).",
  "revenue.estimates": "Proceeds estimate on Monetisation → Revenue: Apple's commission (0.15 under the Small Business Program), Stripe's fee percent and fixed USD fee per charge.",
  "app.ios": "iOS versions: auto (follow the App Store), latest, minimum (below it the app can't be used), App Store link, prompt cadence, release notes per version. Easier on App updates.",
  "index.free": "Free players see their Logic Index, tier, standing, strongest skill and the curve, and can share them (false: Pro only). Easier on Sharing → Settings.",
  "index.minPlayers": "Below this many index holders (1–100000), players' standing and the curve are hidden. Easier on Sharing → Settings.",
  "share.appStoreCampaign": "{ providerToken }: the App Store Connect provider token (digits) for campaign links (pt=, ct=) on the web's pages; null: plain store links.",
  "rating.prompt": "The StoreKit rating prompt: on/off, after N active days and sessions, cooldown, once per version, the moments it may ask at. Easier on App updates.",
};

const groupOf = (key: string) => key.split(".")[0] ?? key;
const kind = (v: JsonValue) => (v === null ? "null" : Array.isArray(v) ? "array" : typeof v);

function parse(text: string): { ok: true; value: JsonValue } | { ok: false; error: string } {
  try {
    return { ok: true, value: JSON.parse(text) as JsonValue };
  } catch {
    return { ok: false, error: "Not valid JSON. Strings need quotes: \"text\"." };
  }
}

export function ConfigPage() {
  const q = useConfig();
  const editable = useCan("config.edit");
  const { admin } = useAuth();
  const [pending, setPending] = useState<{ key: string; from: JsonValue | undefined; to: JsonValue } | null>(null);
  const mutation = useSetConfig();
  const { hash } = useLocation();
  const loaded = q.isSuccess;
  useEffect(() => {
    // /config#arena (linked from the Arena page) lands on that group.
    if (loaded && hash) document.getElementById(`config-${hash.slice(1)}`)?.scrollIntoView?.({ block: "start" });
  }, [loaded, hash]);

  const header = (
    <PageHeader
      title="Config"
      meta={
        editable ? (
          <span>Runtime settings read by the API. Values are validated per key; every change is written to the audit log.</span>
        ) : (
          <span className={styles.readOnly} data-testid="read-only">
            Read-only · editing config needs the admin role (you are {admin?.role})
          </span>
        )
      }
    />
  );

  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={8} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );

  const entries = Object.entries(q.data.values).sort(([a], [b]) => a.localeCompare(b));
  const request = (key: string, to: JsonValue) => {
    mutation.reset();
    setPending({ key, from: q.data.values[key], to });
  };

  return (
    <>
      {header}
      <div className={tableStyles.wrap}>
        <table className={tableStyles.table}>
          <thead>
            <tr>
              <th style={{ width: "32%" }}>Key</th>
              <th style={{ width: 80 }}>Type</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, value], i) => (
              <Fragment key={key}>
                {i === 0 || groupOf(entries[i - 1]![0]) !== groupOf(key) ? (
                  <tr className={styles.configGroup} data-testid={`config-group-${groupOf(key)}`} id={`config-${groupOf(key)}`}>
                    <td colSpan={3}>{groupOf(key)}</td>
                  </tr>
                ) : null}
                <ConfigRow name={key} value={value} editable={editable} onSave={(v) => request(key, v)} />
              </Fragment>
            ))}
          </tbody>
        </table>
        {entries.length === 0 ? <EmptyState title="No config values">The API reports no settings.</EmptyState> : null}
      </div>

      <ConfirmDialog
        open={pending !== null}
        title="Change config value?"
        confirmLabel="Save"
        busy={mutation.isPending}
        error={mutation.error}
        onCancel={() => setPending(null)}
        onConfirm={() =>
          pending && mutation.mutate({ key: pending.key, value: pending.to }, { onSuccess: () => setPending(null) })
        }
      >
        {pending ? (
          <div className={styles.diff}>
            <code>{pending.key}</code>
            <div className={styles.diffRow} data-kind="from">
              <span className="monoLabel">before</span>
              <code>{pending.from === undefined ? "(unset)" : show(pending.from)}</code>
            </div>
            <div className={styles.diffRow} data-kind="to">
              <span className="monoLabel">after</span>
              <code>{show(pending.to)}</code>
            </div>
          </div>
        ) : null}
      </ConfirmDialog>
    </>
  );
}

function ConfigRow({
  name,
  value,
  editable,
  onSave,
}: {
  name: string;
  value: JsonValue;
  editable: boolean;
  onSave: (v: JsonValue) => void;
}) {
  const [text, setText] = useState(show(value));
  const [base, setBase] = useState(show(value));
  if (base !== show(value)) {
    // The server value changed (e.g. after a save): reset the draft.
    setBase(show(value));
    setText(show(value));
  }
  const parsed = parse(text);
  const dirty = text.trim() !== show(value);
  return (
    <tr>
      <td className={tableStyles.mono}>
        {name}
        {DESCRIPTIONS[name] ? <span className={styles.configDesc}>{DESCRIPTIONS[name]}</span> : null}
      </td>
      <td>
        <Chip tone="muted">{kind(value)}</Chip>
      </td>
      <td>
        {name === "arena.schedule" && isTimeList(value) ? (
          <ScheduleEditor value={value} editable={editable} onSave={onSave} />
        ) : editable ? (
          <form
            className={styles.configEdit}
            onSubmit={(e) => {
              e.preventDefault();
              if (parsed.ok && dirty) onSave(parsed.value);
            }}
          >
            <TextInput
              aria-label={`Value for ${name}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className={styles.configInput}
              aria-invalid={!parsed.ok || undefined}
              spellCheck={false}
            />
            {typeof value === "boolean" && !dirty ? (
              <Button size="sm" variant="secondary" onClick={() => onSave(!value)} aria-label={`${value ? "Turn off" : "Turn on"} ${name}`}>
                {value ? "Turn off" : "Turn on"}
              </Button>
            ) : null}
            {dirty ? (
              <>
                <Button size="sm" type="submit" variant="primary" disabled={!parsed.ok}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setText(show(value))}>
                  Undo
                </Button>
              </>
            ) : null}
            {!parsed.ok ? <span className={styles.fieldError}>{parsed.error}</span> : null}
          </form>
        ) : (
          <code className={styles.configValue}>{show(value)}</code>
        )}
      </td>
    </tr>
  );
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const isTimeList = (v: JsonValue): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string");
const sortTimes = (list: string[]) => [...new Set(list)].sort();

const localFmt = new Intl.DateTimeFormat(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" });
const utcFmt = new Intl.DateTimeFormat("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit", timeZone: "UTC", hour12: false });

/**
 * `arena.schedule`: the UTC drop times as removable chips, a time field to add one, and the next
 * three drops (UTC and the viewer's local time). Saving goes through the page's confirm step.
 */
function ScheduleEditor({ value, editable, onSave }: { value: string[]; editable: boolean; onSave: (v: JsonValue) => void }) {
  const [draft, setDraft] = useState<string[]>(value);
  const [base, setBase] = useState(JSON.stringify(value));
  const [time, setTime] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (base !== JSON.stringify(value)) {
    setBase(JSON.stringify(value));
    setDraft(value);
  }
  const sorted = sortTimes(draft);
  const dirty = JSON.stringify(sorted) !== JSON.stringify(value);
  const upcoming = nextDrops(sorted, new Date(), 3);
  const add = () => {
    const t = time.trim();
    if (!HHMM.test(t)) return setError("Use a 24-hour time, HH:MM.");
    if (sorted.includes(t)) return setError(`${t} is already on the schedule.`);
    if (sorted.length >= 48) return setError("At most 48 drops a day.");
    setDraft(sortTimes([...sorted, t]));
    setTime("");
    setError(null);
  };
  return (
    <div className={styles.schedule} data-testid="arena-schedule">
      <div className={styles.scheduleTimes} role="list" aria-label="Drop times (UTC)">
        {sorted.map((t) => (
          <span key={t} role="listitem" className={styles.scheduleTime} data-new={!value.includes(t) || undefined}>
            <span className="mono">{t}</span>
            {editable ? (
              <button type="button" aria-label={`Remove ${t}`} onClick={() => setDraft(sorted.filter((x) => x !== t))} disabled={sorted.length === 1}>
                ×
              </button>
            ) : null}
          </span>
        ))}
        <span className="monoLabel">UTC</span>
      </div>
      {editable ? (
        <form
          className={styles.configEdit}
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <TextInput type="time" aria-label="Add a drop time (UTC)" value={time} onChange={(e) => setTime(e.target.value)} className={styles.scheduleInput} step={60} />
          <Button size="sm" type="submit" variant="secondary" disabled={!time}>
            Add
          </Button>
          {dirty ? (
            <>
              <Button size="sm" variant="primary" onClick={() => onSave(sorted)}>
                Save schedule
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft(value);
                  setError(null);
                }}
              >
                Undo
              </Button>
            </>
          ) : null}
          {error ? <span className={styles.fieldError}>{error}</span> : null}
        </form>
      ) : null}
      {upcoming.length ? (
        <span className={styles.configDesc} data-testid="next-drops">
          Next: {upcoming.map((d) => `${utcFmt.format(d)} UTC (${localFmt.format(d)} local)`).join(" · ")}
        </span>
      ) : null}
    </div>
  );
}
